import React, { useState } from 'react';
import { FormattedDate, FormattedMessage, FormattedNumber, injectIntl } from 'react-intl';
import SorterList from '../../components/SorterList';
import { formatDateYYYYMMDD, IMonthValue } from '../../util/DateUtils';
import Modal from '../../components/Modal';
import { IHandlerData } from '../../util/DataModelInterface';
import * as handlerdata from '../../api/HandlerData';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { getPreviousMonth } from '../../util/DateUtils';
import { exportCompareSummaryReport, ReportRow } from '../../api/SummaryReport';
import { Alert } from 'react-bootstrap';
import { getAllCollectionpointInfo, getCollectionpointOperators } from '../../api/CollectionpointOperators';
import ReportTypeList from '../../components/ReportTypeList';
import DateSelector from '../../components/DateSelector';

interface IBrowseHandlerReportProps {
  intl: any;
}

interface IColumn {
  name: string;
  sortable: boolean;
}

const COLUMNS = [
   { name: 'handlerid', sortable: false },
   { name: 'handlername', sortable: false },
   { name: 'stationid', sortable: false },
   { name: 'stationname', sortable: false },
   { name: 'operatorid', sortable: false },
   { name: 'operatorname', sortable: false },
   { name: 'collectionpointid', sortable: true },
   { name: 'collectionpointname', sortable: true },
   { name: 'category', sortable: true },
   { name: 'type', sortable: true },
   { name: 'date', sortable: true },
   { name: 'weightkg', sortable: true },
   { name: 'documentid', sortable: true }
] as IColumn[];

const BrowseHandlerReport = (props: IBrowseHandlerReportProps) => {
  const [,setState] = useState({}); // just to force re-render when upload status changes
  const [startDate, setStartDate] = useState(getPreviousMonth());
  const [endDate, setEndDate] = useState(getPreviousMonth());
  const [modalOpen, setModalOpen] = useState(false);
  const [sorter, setSorter] = useState('');
  const [sorterName, setSorterName] = useState('');
  const [types, setTypes] = useState([] as {label: string, value: string}[]);
  const [dataTable, setData] = useState([] as IHandlerData[]);
  const [orderColumn, setOrderColumn] = useState('');
  const [orderDirection, setOrderDirection] = useState(1); // 1 | -1
  const [showAlert, setShowAlert] = useState(false);

  const getModal = () =>
    <Modal
      onClose={() => setModalOpen(false)}
      closeOnClickOutside={true}
      width={400}
      height={250}
    >
      <div className="ModalView">
        <div className="spinner"/>
        <div className="message"><FormattedMessage id="handlerreports.browse.getting"/></div>
      </div>
    </Modal>

  const comparer = (a: IHandlerData, b: IHandlerData) => {
    let value = 0;
    if (orderColumn !== '') {
      switch(orderColumn) {
        case 'operatorid': // not visible
        case 'opertorname': // not visible
        case 'handlerid': // always the same
        case 'handlername': // always the same
        case 'stationid': // not visible
        case 'stationname': // not visible
          value = 0;
          break;
        case 'collectionpointid':
          value = parseInt(a.collectionPoint.id) - parseInt(b.collectionPoint.id);
          break;
        case 'collectionpointname':
          value = a.collectionPoint.name.localeCompare(b.collectionPoint.name);
          break;
        case 'category':
          value = a.category.localeCompare(b.category);
          break;
        case 'type':
          value = a.itemType.localeCompare(b.itemType);
          break;
        case 'date':
          const dateA = new Date(a.date);
          const dateB = new Date(b.date);
          value = dateA.getTime() - dateB.getTime();
          break;
        case 'weightkg':
          value = parseInt(a.weight) - parseInt(b.weight);
          break;
        case 'documentid':
          value = parseInt(a.transferdocumentId) - parseInt(b.transferdocumentId);
          break;
      }
    }
    return orderDirection * value;
  }

  const getDataTable = () => {

    const data: { }[] = [];

    if (dataTable && Array.isArray(dataTable)) {
      dataTable.sort(comparer).forEach((row : IHandlerData, index) => {
        data.push(<tr key={index}>
                        <td>{row.collectionPoint.id}</td>
                        <td>{row.collectionPoint.name}</td>
                        <td>{row.category}</td>
                        <td>{row.itemType}</td>
                        <td>{new Date(row.date).toLocaleDateString('fi-FI')}</td>
                        <td><FormattedNumber value={parseInt(row.weight)} maximumFractionDigits={0} minimumFractionDigits={0}/></td>
                        <td>{row.transferdocumentId}</td>
                     </tr>);
      });
    }
    return data;
  }

  const clearData = () => {
    setStartDate(getPreviousMonth());
    setEndDate(getPreviousMonth());
    setModalOpen(false);
    setSorter('');
    setTypes([]);
    setShowAlert(false);
    setData([] as IHandlerData[]);
  }

  const setOrdering = (column: string) => {
    if (orderColumn !== column) {
      setOrderColumn(column);
      setOrderDirection(1);
    } else {
      setOrderDirection(-orderDirection);
    }
  }

  const getTableHeaders = () => {
    const headers: JSX.Element[] = [];
    COLUMNS.forEach((col, i) => {
      const classes = ['header'];
      let icon = null;
      if (col.name === orderColumn) {
        classes.push('sortby');
        icon = orderDirection === 1 ?
          <FontAwesomeIcon className="icon" icon="sort-up" color="white" /> : <FontAwesomeIcon className="icon" icon="sort-down" color="white" />
      }
      if (col.sortable) {
        classes.push('sortable');
        icon = <FontAwesomeIcon className="icon" icon="sort" color="lightgray" />
        headers.push(
          <th key={i}><span className={classes.join(' ')} onClick={() => col.sortable && setOrdering(col.name)}>
            <FormattedMessage id={`handlerreports.browse.datatable.${col.name}`}/> {icon}
          </span></th>
        );
      }
    })
    return headers;
  }

  const showDataTable = () => {
    if(dataTable.length > 0 && dataTable && Array.isArray(dataTable)) {
      return(
        <div className="search-results">
          <div className="result-header d-flex flex-row">
            <div className="reportResultHeader">
              <h3 className="monthRange">
                <FormattedDate
                    value={new Date(startDate.year, startDate.month)}
                    month="long"
                    year="numeric"/>
                -
                <FormattedDate
                    value={new Date(endDate.year, endDate.month)}
                    month="long"
                    year="numeric"/>
              </h3>
              <FormattedMessage id="handlerreports.header"/>
            </div>
            <div className="handlerNameHeader ml-auto"><h1>{sorterName}<br/><div className="label">{sorter}</div></h1></div>
          </div>

          <div className="table-responsive">
            <table className="table table-sm table-striped">
              <thead className="reportDataHeader table table-bordered table-sm">
                <tr>
                  { getTableHeaders() }
                </tr>
              </thead>
              <tbody className="reportData table table-bordered">
                  { getDataTable() }
              </tbody>
            </table>
          </div>
        </div>);
    } else if (showAlert) {
      return(
        <Alert variant="warning">
          <FormattedMessage id="receptionreports.browse.noreportsfound" />
        </Alert>);
    } else {
      return(<></>);
    }
  }

  const formatDataForExport = async (data: IHandlerData[]) => {
    let formatted = [] as ReportRow[];
    let collectionpoints = await getAllCollectionpointInfo();
    let operators = await getCollectionpointOperators();
    if (data && Array.isArray(data)) {
      data.sort(comparer).forEach((d: IHandlerData) => {
        let row = {} as ReportRow;
        COLUMNS.forEach((col) => {
          let key = props.intl.formatMessage({id:`handlerreports.browse.datatable.${col.name}`});
          let cp = collectionpoints.find((cp) => cp.info.collectionpoint_id === d.collectionPoint.id);
          switch(col.name) {
            case 'handlerid':
              row[key] = d.sorter.id;
              break;
            case 'handlername':
              row[key] = d.sorter.name;
              break;
            case 'stationid':
              row[key] = d.sortStation.id;
              break;
            case 'stationname':
              row[key] = d.sortStation.name;
              break;
            case 'operatorid':
              row[key] = cp?.info.operator_id;
              break;
            case 'operatorname':
              let op = operators.find((op) => op.id === cp?.info.operator_id);
              row[key] = op?.name;
              break;
            case 'collectionpointid':
              row[key] = d.collectionPoint.id;
              break;
            case 'collectionpointname':
              row[key] = d.collectionPoint.name;
              break;
            case 'date':
              row[key] = new Date(d.date).toLocaleDateString('fi-FI');
              break;
            case 'type':
              row[key] = d.itemType;
              break;
            case 'weightkg':
              row[key] = parseFloat(d.weight);
              break;
            case 'documentid':
              row[key] = d.transferdocumentId;
              break;
            default:
              row[key] = d[col.name as keyof typeof d];
              break;
          }
        })
        formatted.push(row);
      });
    }
    return formatted;
  }

  return (
    <div>
      { modalOpen && getModal() }
      <h1>
        <FormattedMessage id="handlerreports.header"/> - <FormattedMessage id="handlerreports.browse.header"/>
      </h1>
      <div className="search-wrap">
        <div className="d-flex flex-row row">
          <div className="col-md-6">
            <div className="form-group">
              <label htmlFor="sorter-select"><FormattedMessage id="handlerreports.browse.sorter"/></label>
              <select className="sorter-select form-control" id="sorter-select"
                value={sorter}
                onChange={(e) => {
                  setSorter(e.target.value);
                  setSorterName(e.target.options[e.target.selectedIndex].text);
                }}>
                { SorterList(props.intl) }
              </select>
            </div>
          </div>
          <div className="col-md-6">
            <div className="form-group">
              <label htmlFor="sorter-select"><FormattedMessage id="handlerreports.browse.reporttypes"/></label>
              <ReportTypeList sorter={sorter} multiselect={true} setType={(selected: any) => setTypes(selected)}/>
            </div>
          </div>
          <div className="col-md-12">
          <div className="form-group">
            <label><FormattedMessage id="handlerreports.browse.monthrange"/></label>
            <div className="d-flex flex-row row">
              <div className="col-md-6 datepicker-input">
                <DateSelector startPeriod={startDate} endPeriod={endDate}
                              onChange={(start: IMonthValue, end: IMonthValue) => { setStartDate(start); setEndDate(end); } } 
                              dateFormat={'yyyy-MM'} isStartAndEnd={true} />
              </div>
            </div>
          </div>
          </div>

          <div className="form-buttons d-flex flex-row col">
            <button className="btn btn-primary big-send-button"
                    disabled={dataTable.length === 0}
                    onClick={async () => exportCompareSummaryReport('handler',
                            { header: props.intl.formatDate(new Date(startDate.year, startDate.month), {month: "long", year: "numeric"}) + '-' +
                                      props.intl.formatDate(new Date(endDate.year, endDate.month), {month: "long", year: "numeric"}),
                              data: await formatDataForExport(dataTable),
                              compareHeader: "", compareData: {}})}>
              <FormattedMessage id="comparereport.datatable.export"/>
            </button>
            <button className="btn btn-secondary clear-button ml-auto btn-search-reset" onClick={() => { clearData();  }}>
              <FormattedMessage id="handlerreports.browse.clear"/>
            </button>
            <button className="btn btn-primary send-button btn-search"
                    disabled={types === null || types.length === 0 || sorter === ''}
                    onClick={() => {
                      setModalOpen(true);
                      handlerdata.getHandlerData(sorter, types.map((t) => t.value), 
                                                 formatDateYYYYMMDD(startDate), 
                                                 formatDateYYYYMMDD(endDate, true))
                      .then((d:IHandlerData[]) => {
                        setData(d as IHandlerData[]);
                        setShowAlert(d.length===0);
                      }).finally(() => {
                        setState({});
                        setModalOpen(false);
                      });}}>
              <FormattedMessage id="handlerreports.browse.get"/>
            </button>
          </div>
        </div>
      </div>
      {showDataTable()}
    </div>
  );
}

export default injectIntl(BrowseHandlerReport);
